import { useRef } from "react";
import ArrowSubtitle from "../ArrowSubtitle/ArrowSubtitle";
import TestimonialCard from "../TestimonialCard/TestimonialCard";
import styles from "./Carousel.module.css";
import { useEffect } from "react";
import { useCallback } from "react";
import { useLayoutEffect } from "react";
import { useState } from "react";
import useIntersectionObserver from "../../hooks/useIntersectionObserver";

function Carousel({ data, heading, arrowText, darkArrowSubtitle }) {
    const [containerRef, , fadeInClassName] = useIntersectionObserver(0.2);
    const [movement, setMovement] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const previousTouchX = useRef(0);
    const velocityRef = useRef(0);
    const lastTimeRef = useRef(0);
    const friction = 0.95;
    const minVelocity = 0.01;

    const animateInertia = useCallback(() => {
        if (Math.abs(velocityRef.current) > minVelocity) {
            setMovement((prev) => prev + velocityRef.current * 16);
            velocityRef.current *= friction;
            requestAnimationFrame(animateInertia);
        }
    }, []);

    // when movement changes
    useLayoutEffect(() => {
        const children = containerRef.current.children;

        if (movement < -386) {
            const first = children[0];
            containerRef.current.append(first);
            setMovement(0);
        } else if (movement > 386) {
            const last = children[children.length - 1];
            containerRef.current.prepend(last);
            setMovement(0);
        } else {
            containerRef.current.style.setProperty(
                "--drag-translation",
                movement + "px"
            );
        }
    }, [movement, containerRef]);

    function handleLeft() {
        if (!isTransitioning) {
            setIsTransitioning(true);

            const children = containerRef.current.children;

            const last = children[children.length - 1];

            setTimeout(() => {
                containerRef.current.classList.add(
                    styles.transition,
                    styles.move_left
                );

                setMovement((prev) => {
                    return Math.round(prev / 386) * 386;
                });
            }, 0);

            setTimeout(() => {
                containerRef.current.classList.remove(
                    styles.transition,
                    styles.move_left
                );
                containerRef.current.prepend(last);
                setIsTransitioning(false);
            }, 200);
        }
    }
    function handleRight() {
        if (!isTransitioning) {
            setIsTransitioning(true);
            containerRef.current.classList.add(
                styles.transition,
                styles.move_right
            );

            setMovement((prev) => {
                return Math.round(prev / 386) * 386;
            });

            const first = containerRef.current.children[0];

            setTimeout(() => {
                containerRef.current.classList.remove(
                    styles.transition,
                    styles.move_right
                );

                containerRef.current.append(first);
                setIsTransitioning(false);
            }, 200);
        }
    }

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);

        previousTouchX.current =
            e?.touches?.[0]?.clientX || previousTouchX.current;
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            e.preventDefault();
            if (isDragging) {
                const now = Date.now();
                const dt = now - lastTimeRef.current;
                const dx = e.movementX;

                velocityRef.current = dx / dt;

                setMovement((prev) => prev + dx);
                lastTimeRef.current = now;
            }
        },
        [isDragging]
    );

    const handleMouseUp = useCallback(
        (e) => {
            e.preventDefault();
            setIsDragging(false);
            animateInertia();

            previousTouchX.current = null;
        },
        [animateInertia]
    );

    const handleTouchMove = useCallback(
        (e) => {
            const currentTouchX =
                e.touches[0]?.clientX || previousTouchX.current;
            const movementX = currentTouchX - previousTouchX.current;

            e.preventDefault();
            if (isDragging) {
                const now = Date.now();
                const dt = now - lastTimeRef.current;
                const dx = movementX;

                velocityRef.current = dx / dt;

                setMovement((prev) => prev + dx);
                lastTimeRef.current = now;
            }

            previousTouchX.current = currentTouchX;
        },
        [isDragging]
    );

    useEffect(() => {
        const container = containerRef.current;
        container.addEventListener("mousedown", handleMouseDown);
        container.addEventListener("mousemove", handleMouseMove);
        container.addEventListener("mouseup", handleMouseUp);
        container.addEventListener("touchstart", handleMouseDown);
        container.addEventListener("touchmove", handleTouchMove);
        container.addEventListener("touchend", handleMouseUp);

        return () => {
            container.removeEventListener("mousedown", handleMouseDown);
            container.removeEventListener("mousemove", handleMouseMove);
            container.removeEventListener("mouseup", handleMouseUp);
            container.removeEventListener("touchstart", handleMouseDown);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleMouseUp);
        };
    }, [
        handleMouseUp,
        handleMouseDown,
        handleMouseMove,
        handleTouchMove,
        containerRef,
    ]);

    return (
        <div className={[styles.carousel, fadeInClassName].join(" ")}>
            <div className={styles.left}>
                <div className={styles.text}>
                    <ArrowSubtitle text={arrowText} dark={darkArrowSubtitle} />
                    <h2 className={styles.heading}>{heading}</h2>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 20,
                    }}
                >
                    <div className={styles.buttons}>
                        <button
                            onClick={handleLeft}
                            aria-label="Previous posts"
                            className={[styles.button, styles.left_button].join(
                                " "
                            )}
                        >
                            <img src="/images/left-arrow-navy.svg" alt="" />
                        </button>
                        <button
                            onClick={handleRight}
                            aria-label="Next posts"
                            className={[
                                styles.button,
                                styles.right_button,
                            ].join(" ")}
                        >
                            <img src="/images/right-arrow-navy.svg" alt="" />
                        </button>
                    </div>
                </div>
            </div>
            <div
                className={[
                    styles.right_overflow_container,
                    styles.fade_cover,
                ].join(" ")}
            >
                <div className={styles.posts_container} ref={containerRef}>
                    {data.map((value, index) => (
                        <TestimonialCard key={index} {...value} />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Carousel;
